import { MessendzheryKlientki } from '../obrashcheniya/components/MessendzheryKlientki';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { IconChevronDown, IconPhoneIncoming } from '@tabler/icons-react';
import { useAuth } from '../auth/useAuth';
import { usePermissions } from '../auth/usePermissions';
import { MaskaTelefona } from '../hodba/MaskaTelefona';
import { formatDuration } from './statusVocabulary';
import { usePhone } from './usePhone';
import { useWorkplaceState, WORKPLACE_STATE_KEY } from './useWorkplaceState';
import { chuzhoyZvonok, naborObzvona, svoyPoServeru } from './oknoZvonka';
import { zvonokVhodyashchego } from './zvonokVhodyashchego';
import './okno-zvonka.css';

/**
 * s766 (обращение техподдержки №4, инициатор — менеджер Наталья): ОКНО ВХОДЯЩЕГО ЗВОНКА.
 *
 * Жалоба менеджеров 10.10.2026: «непонятно, когда поступил звонок и что это за звонок». Браузер
 * снимает трубку сам, а сведения о звонке появлялись в шапке через секунду-две, только в открытой
 * вкладке и только там, где шапка есть. Окно всплывает В ТУ ЖЕ СЕКУНДУ, когда вызов пришёл в
 * браузер, на ЛЮБОЙ странице панели (оно живёт рядом с телефоном, над маршрутами) и называет
 * линию, номер, обзвон, клиентку и её ближайшую запись.
 *
 * ⛔ Окно — только про показ. «Скрыть окно» сворачивает его в плашку; разговор, микрофон и динамик
 * оно не трогает вовсе: соединение держит телефон в `PhoneProvider`, кнопка завершения — в шапке.
 *
 * Телефон этого человека может держать другое окно браузера: тогда о вызове говорит оно (общий
 * канал окон), а если канала нет — звонок всё равно виден по ответу сервера, чуть позже.
 */

/** Сколько ждём сведений сервера о вызове, которого в этом окне не слышно. */
const ZHDAT_SERVER_MS = 6000;
/** После скольких секунд без сведений перестаём писать «определяю». */
const OPREDELYAYU_MS = 4000;

interface Epizod {
  nachalo: number;
  skryt: boolean;
  /** Ключи звонка, под которыми сервер называл его в этом эпизоде. */
  klyuchi: string[];
  /** Звонок уже видели живым: пропал — значит кончился, ждать нечего. */
  bylZhiv: boolean;
  /** Вызов шёл в ЭТОМ окне: его конец знает сам браузер, раньше сервера. */
  lokalnoBylo: boolean;
  /** Окно поднял ответ сервера, а не вызов в браузере. */
  poSostoyaniyu: boolean;
}

type Soobshchenie = { t: 'vyzov' | 'konec'; at: number } | { t: 'skryt'; v: boolean };

export function OknoZvonka() {
  const { user } = useAuth();
  const { can, sectionHidden } = usePermissions();
  const allowed = user != null && can('tel.workplace') && !sectionHidden('tel-workplace');
  const { state } = useWorkplaceState(allowed);
  const { phone } = usePhone();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [epizod, setEpizod] = useState<Epizod | null>(null);
  const [zakrytye, setZakrytye] = useState<string[]>([]);
  const [vidennyySeq, setVidennyySeq] = useState(0);
  // Вызов и конец разговора, о которых сказало соседнее окно с телефоном.
  const [sosedVyzov, setSosedVyzov] = useState(0);
  const [prinyatVyzov, setPrinyatVyzov] = useState(0);
  const [sosedKonec, setSosedKonec] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  const vhod = phone.vhod ?? null;
  const lokalno = phone.callState === 'dialing' || phone.callState === 'early' || phone.callState === 'talking';
  const poServeru = allowed ? chuzhoyZvonok(state) : null;
  const chuzhoy = poServeru !== null && !zakrytye.includes(poServeru.klyuch) ? poServeru : null;
  const nabor = allowed ? naborObzvona(state) : null;

  const novyy = (nachalo: number, poSostoyaniyu: boolean): Epizod => ({
    nachalo, skryt: false, klyuchi: chuzhoy !== null ? [chuzhoy.klyuch] : [], bylZhiv: poSostoyaniyu, lokalnoBylo: false, poSostoyaniyu,
  });

  /*
   * Состояние окна выводится из трёх источников прямо при отрисовке — без эффектов, которые
   * догоняли бы экран следующим ходом: вызов в этом браузере, слово соседнего окна, ответ сервера.
   */
  if (vhod !== null && vhod.seq !== vidennyySeq) {
    // 1. Вызов пришёл в этот браузер. Свой собственный звонок окна не поднимает.
    setVidennyySeq(vhod.seq);
    if (allowed && !vhod.svoy) {
      if (epizod !== null) setZakrytye(epizod.klyuchi);
      setEpizod(novyy(vhod.at, false));
    }
  } else if (sosedVyzov !== prinyatVyzov) {
    // 2. О вызове сказало окно, которое держит телефон.
    setPrinyatVyzov(sosedVyzov);
    if (allowed && epizod === null) setEpizod(novyy(sosedVyzov, false));
  } else if (epizod === null) {
    // 3. Звонок виден по ответу сервера (телефон в другом браузере или канала окон нет).
    if (chuzhoy !== null) setEpizod(novyy(now, true));
  } else {
    const zhiv = lokalno || chuzhoy !== null || nabor !== null;
    const konchilsya = (epizod.lokalnoBylo && !lokalno)
      || sosedKonec > epizod.nachalo
      || (svoyPoServeru(state) && chuzhoy === null)
      || (!zhiv && (epizod.bylZhiv || now - epizod.nachalo > ZHDAT_SERVER_MS));

    if (konchilsya) {
      // Закрытое окно по тому же звонку не всплывает, даже если сервер ещё секунду его показывает.
      setZakrytye(poServeru !== null && !epizod.klyuchi.includes(poServeru.klyuch) ? [...epizod.klyuchi, poServeru.klyuch] : epizod.klyuchi);
      setEpizod(null);
    } else {
      const novyyKlyuch = chuzhoy !== null && !epizod.klyuchi.includes(chuzhoy.klyuch);
      if (novyyKlyuch || (zhiv && !epizod.bylZhiv) || (lokalno && !epizod.lokalnoBylo)) {
        setEpizod({
          ...epizod,
          klyuchi: novyyKlyuch && chuzhoy !== null ? [...epizod.klyuchi, chuzhoy.klyuch] : epizod.klyuchi,
          bylZhiv: epizod.bylZhiv || zhiv,
          lokalnoBylo: epizod.lokalnoBylo || lokalno,
        });
      }
    }
  }

  // Часы окна: длительность и сроки ожидания считаются от них.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);

    return () => window.clearInterval(timer);
  }, []);

  // Общий канал окон этого человека: окно с телефоном говорит остальным о вызове и его конце.
  const kanalRef = useRef<BroadcastChannel | null>(null);
  const userId = user?.id ?? null;
  useEffect(() => {
    if (!allowed || userId === null || typeof BroadcastChannel === 'undefined') return;
    const kanal = new BroadcastChannel(`opsmind.tel.zvonok.${userId}`);
    kanalRef.current = kanal;
    kanal.onmessage = (event: MessageEvent<Soobshchenie>) => {
      const s = event.data;
      if (s.t === 'vyzov') setSosedVyzov(s.at);
      else if (s.t === 'konec') setSosedKonec(s.at);
      else if (s.t === 'skryt') setEpizod((e) => (e === null ? e : { ...e, skryt: s.v }));
    };

    return () => { kanalRef.current = null; kanal.close(); };
  }, [allowed, userId]);
  const skazat = useCallback((s: Soobshchenie) => {
    try { kanalRef.current?.postMessage(s); } catch { /* канал закрыт — соседние окна увидят звонок по серверу */ }
  }, []);

  const vyzovSeq = vhod !== null && !vhod.svoy ? vhod.seq : 0;
  const vyzovAt = vhod?.at ?? 0;
  useEffect(() => {
    if (allowed && vyzovSeq > 0) skazat({ t: 'vyzov', at: vyzovAt });
  }, [allowed, vyzovSeq, vyzovAt, skazat]);
  useEffect(() => {
    if (!lokalno) return;

    // Разговор в этом окне кончился — соседним окнам ждать сервер незачем.
    return () => skazat({ t: 'konec', at: Date.now() });
  }, [lokalno, skazat]);

  /*
   * Сведения нужны сразу, а состояние спрашивается раз в две секунды и только в открытой вкладке.
   * Пока окно звонка поднято, спрашиваем сами: тут же, вдогонку и дальше — в фоновой вкладке тоже.
   */
  const otkryto = epizod !== null;
  const nachalo = epizod?.nachalo ?? 0;
  useEffect(() => {
    if (!otkryto) return;
    const sprosit = () => { void queryClient.refetchQueries({ queryKey: WORKPLACE_STATE_KEY }); };
    sprosit();
    const vdogonku = [400, 1100].map((ms) => window.setTimeout(sprosit, ms));
    const dalshe = window.setInterval(() => { if (document.hidden) sprosit(); }, 2000);

    return () => { vdogonku.forEach((t) => window.clearTimeout(t)); window.clearInterval(dalshe); };
  }, [otkryto, nachalo, lokalno, queryClient]);

  /*
   * Вызов сочли своим (человек только что нажимал «Позвонить», а тот набор не состоялся), но сервер
   * говорит — это чужой звонок: окно поднялось по его ответу, звонок-сигнал звучит сейчас.
   */
  const dognatSignal = epizod !== null && epizod.poSostoyaniyu && phone.registered
    && vhod !== null && vhod.svoy && Math.abs(epizod.nachalo - vhod.at) < 15_000;
  useEffect(() => {
    if (dognatSignal) zvonokVhodyashchego();
  }, [dognatSignal, nachalo]);

  const skryt = useCallback((v: boolean) => {
    setEpizod((e) => (e === null ? e : { ...e, skryt: v }));
    skazat({ t: 'skryt', v });
  }, [skazat]);

  if (!allowed || epizod === null) return null;

  const words = state?.words?.texts ?? {};
  const word = (name: string) => words[`tel.pult.${name}`] ?? `tel.pult.${name}`;
  const sved = chuzhoy ?? nabor;
  const sekund = Math.max(0, Math.floor((now - (phone.talkStartedAt ?? epizod.nachalo)) / 1000));
  const zagolovok = sved?.vid === 'obzvon' ? word('vhod_obzvon') : word('vhod_title');
  const kto = sved === null
    ? (now - epizod.nachalo < OPREDELYAYU_MS ? word('vhod_looking') : word('client_unknown'))
    : (sved.imya ?? (sved.neskolko ? word('vhod_several') : (sved.vid === 'vhod' ? word('vhod_new') : (sved.nomer ?? word('client_unknown')))));
  const perehod = (url: string) => { skryt(true); navigate(url); };

  if (epizod.skryt) {
    return createPortal(
      <button type="button" className="ops-skin okno-zvonka-plashka" data-okno-zvonka="plashka" onClick={() => skryt(false)}
        aria-label={word('vhod_show')} title={word('vhod_show')}>
        <IconPhoneIncoming size={20} /><span>{word('vhod_talk')}</span><strong>{formatDuration(sekund)}</strong>
      </button>,
      document.body,
    );
  }

  return createPortal(
    <aside className="ops-skin okno-zvonka" data-okno-zvonka="okno" data-vid={sved?.vid ?? 'vhod'} role="status" aria-live="assertive" aria-label={zagolovok}>
      <header className="okno-zvonka-shapka">
        <span className="okno-zvonka-znak"><IconPhoneIncoming size={22} /></span>
        <strong className="okno-zvonka-zagolovok">{zagolovok}</strong>
        <span className="okno-zvonka-chasy">{formatDuration(sekund)}</span>
        <button type="button" className="okno-zvonka-skryt" onClick={() => skryt(true)} aria-label={word('vhod_hide')} title={word('vhod_hide')}>
          <IconChevronDown size={20} />
        </button>
      </header>

      <p className="okno-zvonka-kto" data-okno="kto">{kto}</p>

      {sved !== null && <dl className="okno-zvonka-fakty">
        <div data-okno="nomer"><dt>{word('vhod_number')}</dt><dd>{sved.callId !== null && sved.nomer !== null
          ? <MaskaTelefona masked={sved.nomer} entityType="tel_calls" entityId={sved.callId} bezZvonka />
          : (sved.nomer ?? word('client_unknown'))}</dd></div>
        <div data-okno="liniya"><dt>{word('call_line')}</dt><dd>{sved.liniya ?? word('no_data')}</dd></div>
        {sved.obzvon !== null && <div data-okno="obzvon"><dt>{word('dialer_project')}</dt><dd>{sved.obzvon}</dd></div>}
        {sved.obzvon !== null && sved.popytka !== null && <div data-okno="popytka"><dt>{word('attempt')}</dt>
          <dd>{sved.popytka}{sved.popytokVsego ? ` ${word('attempt_of')} ${sved.popytokVsego}` : ''}</dd></div>}
        {sved.zapis !== null && <div data-okno="zapis"><dt>{word('vhod_booking')}</dt>
          <dd><button type="button" className="okno-zvonka-ssylka" onClick={() => perehod(sved.zapis!.url)}>{sved.zapis.kogda}</button></dd></div>}
        {sved.zhdalSek !== null && sved.zhdalSek >= 5 && <div data-okno="zhdal"><dt>{word('vhod_waited')}</dt><dd>{formatDuration(sved.zhdalSek)}</dd></div>}
        {sved.kommentariy ? <div className="okno-zvonka-shiroko" data-okno="kommentariy"><dt>{word('comment')}</dt><dd>{sved.kommentariy}</dd></div> : null}
      </dl>}

      <footer className="okno-zvonka-knopki">
        {sved?.kartochka != null && <MessendzheryKlientki card={sved.kartochka} />}
        {sved?.kartochka != null && <button type="button" className="okno-zvonka-glavnaya" onClick={() => perehod(sved.kartochka!.url)}>{word('open_card')}</button>}
        <button type="button" onClick={() => skryt(true)}>{word('vhod_hide')}</button>
      </footer>
    </aside>,
    document.body,
  );
}
